import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { WidgetType } from '../enums/widget';
import { Account } from '../interfaces/account';
import { AccountNameUid } from '../interfaces/account-name-uid';
import { DynamicDateRange } from '../interfaces/dynamic-date-range';
import { GraphWidget, HeaderParams, ModeWidgetParameters, UserWidgetParameters } from '../interfaces/widget';
import {
  DeleteUserWidget,
  DeleteUserWidgetResponse,
  UpdateUserWidget,
} from '../schema/mutation-definitions/users_admin.graphql';
import { RelatedAccount } from '../schema/query-definitions/accounts.graphql';
import { UserWidgetResponse, UserWidgets } from '../schema/query-definitions/users_admin.graphql';
import { AccountService } from './account.service';
import { AuthService } from './auth.service';

dayjs.extend(advancedFormat);

@Injectable({
  providedIn: 'root',
})
export class WidgetService implements OnDestroy {
  private activeAccountUid?: string;

  private activeAccountAggregatorEnabled?: boolean;

  private activeAccountAggregatorChildAccountsList: RelatedAccount[] = [];

  private widgets?: GraphWidget[];

  private widgetType?: WidgetType;

  private externalAccess: boolean = false;

  private userWidgetAccountsInstant: AccountNameUid[] = [];

  public widgetsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public userWidgetAccounts$: BehaviorSubject<AccountNameUid[]> = new BehaviorSubject<AccountNameUid[]>([]);

  public activeWidgets$: BehaviorSubject<GraphWidget[] | undefined> = new BehaviorSubject<GraphWidget[] | undefined>(
    undefined,
  );

  public activeSidebarWidgets$: BehaviorSubject<GraphWidget[] | undefined> = new BehaviorSubject<
    GraphWidget[] | undefined
  >(undefined);

  public set activeAccount(uid: string) {
    this.activeAccountUid = uid;
  }

  public set activeAccountAggregator(enabled: boolean) {
    this.activeAccountAggregatorEnabled = enabled;
  }

  public set activeAccountAggregatorChildAccounts(childAccounts: RelatedAccount[]) {
    this.activeAccountAggregatorChildAccountsList = childAccounts;
  }

  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private readonly userWidgets: UserWidgets,
    private readonly updateUserWidgetGQL: UpdateUserWidget,
    private readonly deleteUserWidgetGQL: DeleteUserWidget,
    private readonly auth: AuthService,
    private readonly translate: TranslateService,
    //TODO: remove anti-pattern
    private readonly accountService: AccountService,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getDynamicDateRanges(): DynamicDateRange[] {
    return [
      {
        key: 0,
        title: this.translate.instant('graphEmbed.dynamicDates.static'),
        beginDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 1,
        title: this.translate.instant('graphEmbed.dynamicDates.today'),
        beginDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 2,
        title: this.translate.instant('graphEmbed.dynamicDates.yesterday'),
        beginDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      },
      {
        key: 3,
        title: this.translate.instant('graphEmbed.dynamicDates.thisWeek'),
        beginDate: dayjs().startOf('week').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 4,
        title: this.translate.instant('graphEmbed.dynamicDates.lastWeek'),
        beginDate: dayjs().subtract(7, 'day').startOf('week').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(7, 'day').endOf('week').format('YYYY-MM-DD'),
      },
      {
        key: 5,
        title: this.translate.instant('graphEmbed.dynamicDates.thisMonth'),
        beginDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 6,
        title: this.translate.instant('graphEmbed.dynamicDates.lastMonth'),
        beginDate: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      },
      {
        key: 7,
        title: this.translate.instant('graphEmbed.dynamicDates.lastMonthToDate'),
        beginDate: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 8,
        title: this.translate.instant('graphEmbed.dynamicDates.thisQuarter'),
        beginDate: dayjs().startOf('quarter').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 9,
        title: this.translate.instant('graphEmbed.dynamicDates.lastQuarter'),
        beginDate: dayjs().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD'),
      },
      {
        key: 10,
        title: this.translate.instant('graphEmbed.dynamicDates.thisYear'),
        beginDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        key: 11,
        title: this.translate.instant('graphEmbed.dynamicDates.lastYear'),
        beginDate: dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
      },
      {
        key: 12,
        title: this.translate.instant('graphEmbed.dynamicDates.lastYearToDate'),
        beginDate: dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      },
    ];
  }

  /**
   * Retrieves the user's active widget list and sets the default account UID for followup requests
   * If the account UID is not set, we may not make the request
   * @param widgetType
   * @param activeAccountUid
   */
  setActiveUserWidgets(widgetType?: WidgetType): void {
    if (!this.activeAccountUid) {
      console.error('[error] Active Account UID is not set for graph widgets');
      return;
    }
    if (widgetType) {
      this.widgetType = widgetType;
    }
    const widgets = this.widgets?.filter((e: GraphWidget) => e.widget.type === this.widgetType);
    this.activeWidgets$.next(widgets);
  }

  setActiveSidebarWidgets(): void {
    if (!this.activeAccountUid) {
      console.error('[error] Active Account UID is not set for graph widgets');
      return;
    }

    const widgets = this.widgets?.filter((e: GraphWidget) => e.widget.type === WidgetType.KPI);
    this.activeSidebarWidgets$.next(widgets);
  }

  async getUserWidgets(userUid: string, widgetType: WidgetType): Promise<void> {
    this.widgetsLoading$.next(true);
    this.userWidgets
      .fetch({ uid: userUid }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((response) => response.data.userWidgets.userWidgets),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: async (response: UserWidgetResponse[]) => {
          const userWidgets: GraphWidget[] = response
            .map((e: UserWidgetResponse) => {
              return {
                widget: {
                  uid: e.uid,
                  name: e.name,
                  description: e.description,
                  url: e.url,
                  type: e.type,
                },
                parameters: e.parameters ? this.addToParameterList(e.parameters) : this.getDefaultParameterList(),
                order: +e.order,
                uid: e.userWidgetUid,
                headers: this.getHeaderParameters(e.parameters ? JSON.parse(e.parameters) : null),
              } as GraphWidget;
            })
            .map((widget: GraphWidget) => {
              return {
                ...widget,
                fullUrl: this.appendUrlParameters(
                  widget.widget.url,
                  widget.parameters ?? this.getDefaultParameterList(),
                ),
              };
            })
            .sort((a: GraphWidget, b: GraphWidget) => (a.order > b.order ? 1 : -1));

          // Sets all the widgets for use later
          this.widgets = userWidgets;

          // Updates the active widget list
          this.setActiveUserWidgets(this.widgetType ?? widgetType);

          this.widgetsLoading$.next(false);

          this.setActiveSidebarWidgets();
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  appendWidget(widget: GraphWidget): void {
    this.widgets?.push({
      ...widget,
      fullUrl: this.appendUrlParameters(widget.widget.url, widget.parameters ?? this.getDefaultParameterList()),
    });
    this.setActiveUserWidgets();
    this.setActiveSidebarWidgets();
  }

  deleteUserWidget(userWidgetUid: string): void {
    this.deleteUserWidgetGQL
      .mutate({ uid: userWidgetUid }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: DeleteUserWidgetResponse | null | undefined) => {
          if (response?.deleteUserWidget.success) {
            this.widgets = this.widgets?.filter((e: GraphWidget) => e.uid !== userWidgetUid);
            this.setActiveUserWidgets();
            this.setActiveSidebarWidgets();
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  async initializeUserWidgetAccounts() {
    combineLatest([
      this.auth.externalRole$,
      this.accountService.accounts,
      this.accountService.selectedAccount,
      this.auth.permissionAndOptions,
      this.accountService.getAggregatorAccountEnabled,
      this.accountService.getAggregatorChildAccounts,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([
          externalRole,
          accountList,
          activeAccount,
          permissionsAndOptions,
          aggregatorAccountEnabled,
          childAccounts,
        ]) => {
          if (!activeAccount) {
            return;
          }
          this.activeAccount = activeAccount.uid;
          this.externalAccess = externalRole;
          this.activeAccountAggregator = aggregatorAccountEnabled === true;
          this.activeAccountAggregatorChildAccounts = childAccounts;

          if (!this.externalAccess) {
            this.widgetsLoading$.next(true);
            let permissions = permissionsAndOptions?.permissions;
            if (permissions) {
              let temp: AccountNameUid[] = permissions.map((permission: any) => {
                return {
                  name: permission.accountTitle,
                  uid: permission.accountUid,
                };
              });
              temp = this.filterUserWidgetAccountsByAggregatorChildren(temp);
              this.userWidgetAccountsInstant = temp;
              this.userWidgetAccounts$.next(temp);
            }
            this.widgetsLoading$.next(false);
          }

          if (accountList) {
            // Updates the accounts available to the user in the widget filters
            let accountNameUids = accountList.map((account: Account) => {
              return {
                name: account.account,
                uid: account.uid,
              };
            }) as AccountNameUid[];

            accountNameUids = this.filterUserWidgetAccountsByAggregatorChildren(accountNameUids);

            this.userWidgetAccountsInstant = accountNameUids;
            this.userWidgetAccounts$.next(accountNameUids);
          }
        },
      );
  }

  private filterUserWidgetAccountsByAggregatorChildren(inputAccounts: AccountNameUid[]): AccountNameUid[] {
    if (!this.activeAccountAggregatorEnabled) {
      return inputAccounts;
    }

    const childUids = this.activeAccountAggregatorChildAccountsList.map((one) => one.uid);
    const enabledUids = childUids.concat([this.activeAccountUid!]);

    return inputAccounts.filter((account) => enabledUids.indexOf(account.uid) !== -1);
  }

  updateUserWidgetParameters(widgetUid: string, parameters: UserWidgetParameters): void {
    if (this.widgets) {
      const paramList = this.getDefaultParameterList(parameters) as ModeWidgetParameters;

      // Build the new param list and re-assemble the filter URL
      this.widgets.forEach((widget) => {
        if (widget.uid === widgetUid) {
          widget.parameters = paramList;
          widget.fullUrl = this.appendUrlParameters(widget.widget.url, widget.parameters);
          widget.headers = this.getHeaderParameters(paramList);
        }
      });

      // Updates the active widget list
      this.setActiveUserWidgets();
      this.setActiveSidebarWidgets();

      // Updates the set parameters in the database
      this.updateUserWidgetGQL
        .mutate(
          { uid: widgetUid, input: { parameters: JSON.stringify(this.getDefaultParameterList(parameters)) } },
          { fetchPolicy: 'no-cache' },
        )
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          error: (err: HttpErrorResponse) => {
            console.error(err);
          },
        });
    }
  }

  appendUrlParameters(origin: string, parameters: ModeWidgetParameters): string {
    const url = new URL(origin);
    const requiredParams = this.getDefaultParameterList() as ModeWidgetParameters;
    if (Object.keys(parameters).length === 0) {
      parameters = this.getDefaultParameterList() as ModeWidgetParameters;
    }
    const params = this.getParameterArray({ ...requiredParams, ...parameters });
    return params.length > 0 ? `${url}?${params.join('&')}` : url.href;
  }

  getHeaderParameters(params?: ModeWidgetParameters): HeaderParams {
    if (!params) {
      return {
        Accounts: null,
        VehicleTypes: null,
        DatePeriod: null,
        StartDate: null,
        EndDate: null,
      };
    }

    const ranges = this.getDynamicDateRanges();
    const vehicleType = params.param_item_type;
    const datePeriod =
      typeof params.param_session_dynamic_date_range_key !== 'undefined'
        ? ranges[params.param_session_dynamic_date_range_key].title
        : ranges[5].title;
    const startDate =
      typeof params.param_session_dynamic_date_range_key !== 'undefined' &&
      params.param_session_dynamic_date_range_key > 0
        ? ranges[params.param_session_dynamic_date_range_key].beginDate
        : params.param_session_begin_date;
    const endDate =
      typeof params.param_session_dynamic_date_range_key !== 'undefined' &&
      params.param_session_dynamic_date_range_key > 0
        ? ranges[params.param_session_dynamic_date_range_key].endDate
        : params.param_session_end_date;

    let headers: HeaderParams = {
      Accounts: null,
      VehicleTypes: vehicleType && vehicleType.length > 0 && vehicleType[0] !== 'all' ? vehicleType : null,
      DatePeriod: datePeriod && datePeriod !== 'null' ? datePeriod : null,
      StartDate: dayjs(startDate).format('MMMM Do YYYY') ?? null,
      EndDate: dayjs(endDate).format('MMMM Do YYYY') ?? null,
    };

    this.accountService.accounts.pipe(takeUntil(this.destroyed$)).subscribe((accounts: Account[]) => {
      const accountNames = accounts
        .filter((account: Account) => !!params?.param_session_account_uid?.includes(account.uid))
        .map((account: Account) => account.account)
        .sort((a, b) => (a <= b ? -1 : 1));

      headers.Accounts = accountNames && accountNames.length > 0 ? accountNames : null;
    });

    return headers;
  }

  /**
   * Map parameters to the param names
   * @param parameters
   * @returns
   */
  public getDefaultParameterList(parameters?: UserWidgetParameters): ModeWidgetParameters | {} {
    if (!this.activeAccountUid) {
      return {};
    }

    const ranges = this.getDynamicDateRanges();

    let dynamicDateRangeIndex = parameters?.sessionDynamicDateRangeKey ?? -1;
    const beginDate =
      dynamicDateRangeIndex <= 0
        ? parameters?.sessionBeginDate ?? dayjs().startOf('month').format('YYYY-MM-DD')
        : ranges[dynamicDateRangeIndex].beginDate;
    const endDate =
      dynamicDateRangeIndex <= 0
        ? parameters?.sessionEndDate ?? dayjs().format('YYYY-MM-DD')
        : ranges[dynamicDateRangeIndex].endDate;

    // default dynamicDateRangeIndex to 'thisMonth' if previously unset
    dynamicDateRangeIndex = dynamicDateRangeIndex < 0 ? 5 : dynamicDateRangeIndex;

    return {
      param_item_model: parameters?.itemModel ?? ['all'],
      param_item_type: parameters?.itemType ?? ['all'],
      param_item_make: parameters?.itemMake ?? ['all'],
      param_item_mileage: parameters?.itemMileage ?? ['all'],
      param_account_name: parameters?.accountName ?? ['all'],
      param_account_oem: parameters?.accountOem ?? ['all'],
      param_account_state: parameters?.accountState ?? ['all'],
      param_account_city: parameters?.accountCity ?? ['all'],
      param_account_reserve_amount: parameters?.accountReserveAmount ?? ['all'],
      param_account_force_login: parameters?.accountForceLogin ?? ['all'],
      param_user_zip_code: parameters?.userZipCode ?? ['all'],
      param_user_cookies: parameters?.userCookies ?? ['all'],
      param_session_account_uid: parameters?.sessionAccountUid ?? [this.activeAccountUid],
      param_session_origin_account_uid: parameters?.sessionOriginAccountUid ?? ['all'],
      param_session_entry: parameters?.sessionEntry ?? ['all'],
      param_session_dynamic_date_range_key: dynamicDateRangeIndex,
      param_session_begin_date: beginDate,
      param_session_end_date: endDate,
      max_age: 86400,
    };
  }

  public addToParameterList(parameters: string): ModeWidgetParameters {
    return {
      ...JSON.parse(parameters),
      max_age: 86400,
    };
  }

  private getParameterArray(parameters: ModeWidgetParameters): string[] {
    const params: string[] = [];
    if (this.activeAccountAggregator) {
      parameters = {
        ...parameters,
        param_session_origin_account_uid: [this.activeAccountUid!],
      };
    }
    for (let parameter in parameters) {
      // @ts-ignore
      let newParams = parameters[parameter];

      if (parameter === 'param_session_account_uid') {
        if (this.activeAccountAggregatorEnabled) {
          newParams = ['all'];
        } else if (!this.externalAccess && newParams.length === 1 && newParams[0] === 'all') {
          newParams = this.userWidgetAccountsInstant.map((a) => a.uid);

          // Hard limit to 100 accounts. This prevents non-external users with access to > 100 accounts from getting a URL too long error
          // Based on rough testing, the account limit where I ran into the URL too long error was around 110 accounts.
          // A limit of 100 gives plenty of character space for other parameters without running into the URL too long error.
          if (newParams.length > 100) {
            newParams = newParams.slice(0, 100);
          }
        }
      }

      if (Array.isArray(newParams)) {
        // Empty arrays are not permitted
        if (newParams.length === 0) {
          newParams = ['all'];
        }
        // Build the parameter string
        newParams.forEach((param: string) => params.push(`${parameter}[]=${encodeURIComponent(param)}`));
      } else {
        // Begin and end date are required
        if (!newParams) {
          if (parameter === 'param_session_begin_date') {
            newParams = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
          }
          if (parameter === 'param_session_end_date') {
            newParams = dayjs().format('YYYY-MM-DD');
          }
        }
        params.push(`${parameter}=${encodeURIComponent(newParams)}`);
      }
    }
    return params;
  }
}
